import { Collapse, Flex, Text, theme } from '@/components';
import { useProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import { processFormulaConditionsAmount } from '@/features/project/_utils/convert-request-params';
import {
  type ProjectSearchFormType,
  ProjectSearchSchema,
  initialValueOfProjectSearchForm,
} from '@/features/search/_models/projects-search';
import { useForm } from '@/hooks';
import {
  DownOutlined,
  HistoryOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SearchHistoryModal } from '../../../project/projects/_components/SearchHistoryModal';
import { useCurrentFiscalYear } from '../../_hooks/get-current-fiscal-year';
import { useProjectsSearchStore } from '../../_stores/projects-search-store';
import {
  type ProjectsSearchSubmitType,
  getInitialValueOfOptionalFields,
} from '../../_utils/projects-search';
import { ProjectsSearchFieldTags } from './ProjectsSearchFieldTags';
import { SearchForm } from './SearchForm';

const getYearValue = (year: number | null) => {
  if (!year) {
    return undefined;
  }
  return dayjs().year(year);
};

type Props = {
  initialFiscalYear?: string;
  onSubmit: (values: ProjectsSearchSubmitType) => Promise<void>;
  /** 主にテストで使用する。強制的に上書きされてしまうので取り扱いに注意 */
  forceInitialValues?: Partial<ProjectSearchFormType>;
};

export const ProjectsSearchAccordion: FC<PropsWithChildren<Props>> = ({
  children,
  initialFiscalYear,
  onSubmit,
  forceInitialValues,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const resetFormTrigger = useRef<boolean>(false);
  const [openSearchHistoryModal, setOpenSearchHistoryModal] = useState(false);
  const { cascaderTargetIds, type, openBasicAccordion, setOpenBasicAccordion } =
    useProjectsSearchStore();

  const currentFiscalYear = useCurrentFiscalYear();
  // 支出先の検索ワードをリセットするため。booleanだと一瞬の変化で検知できないため数値で比較
  const [resetCount, setResetCount] = useState(0);
  const { searchQueryStates } = useProjectsPageSearchQueryStates();

  const defaultValues = useMemo<ProjectSearchFormType>(() => {
    const {
      startYear,
      endYear,
      lastImplementedFiscalYearRangeMin,
      lastImplementedFiscalYearRangeMax,
      formulaConditions,
      ...rest
    } = searchQueryStates;

    const initialValueOfOptionalFields = getInitialValueOfOptionalFields({
      searchQueryStates,
      initialFiscalYearByGroup: initialFiscalYear,
    });

    return {
      ...rest,
      nestedOrganizationIds: cascaderTargetIds.organizationIds ?? null,
      nestedOtherResponsibleOrganizationIds:
        cascaderTargetIds.otherResponsibleOrganizationIds ?? null,
      nestedExpenditureBudgetPurposeIds:
        cascaderTargetIds.expenditureBudgetPurposeIds ?? null,
      nestedPolicyIds: cascaderTargetIds.policyIds ?? null,
      period: [getYearValue(startYear), getYearValue(endYear)],
      lastImplementedFiscalYearPeriod: [
        getYearValue(lastImplementedFiscalYearRangeMin),
        getYearValue(lastImplementedFiscalYearRangeMax),
      ],
      formulaConditions: formulaConditions?.length
        ? (processFormulaConditionsAmount(formulaConditions, {
            rsOnly: true,
            direction: 'divide',
          }) ?? formulaConditions)
        : [
            initialValueOfProjectSearchForm(
              currentFiscalYear.data ?? currentFiscalYear.basicFiscalYear,
            ).formulaConditions,
          ],
      fiscalYear: dayjs().year(Number(initialValueOfOptionalFields.fiscalYear)),
      skipAdministrativeReformTeamEvaluation:
        initialValueOfOptionalFields.skipAdministrativeReformTeamEvaluation,
      ...(forceInitialValues ? forceInitialValues : {}),
    };
  }, [
    searchQueryStates,
    cascaderTargetIds,
    initialFiscalYear,
    forceInitialValues,
    currentFiscalYear.data,
    currentFiscalYear.basicFiscalYear,
  ]);

  const formMethod = useForm<ProjectSearchFormType>({
    resolver: zodResolver(ProjectSearchSchema),
  });

  /**

   * 検索履歴からquery parameterを変更した際にformの値がresetされないため。
   * providerが保持しているcascaderTargetIdsなどを再更新する必要も出てくるため、SearchHistoryModal内ではresetしない
   * アコーディオンの変更によって発火してしまうためresetFormTriggerを採用
   */
  useEffect(() => {
    if (!resetFormTrigger.current) {
      formMethod.reset(defaultValues);
      resetFormTrigger.current = true;
    }
  }, [defaultValues, formMethod.reset]);

  const onResetPayment = useCallback(() => {
    setResetCount((v) => v + 1);
  }, []);
  return (
    <FormProvider {...formMethod}>
      <Collapse
        accordion
        activeKey={openBasicAccordion ? '1' : undefined}
        expandIcon={({ isActive }) =>
          isActive ? (
            <DownOutlined style={{ color: token.colorPrimary, fontSize: 12 }} />
          ) : (
            <RightOutlined
              style={{ color: token.colorPrimary, fontSize: 12 }}
            />
          )
        }
        onChange={(e) => {
          setOpenBasicAccordion(!!e.length);
        }}
        style={{
          background: token.colorBgBase,
          borderRadius: 0,
          minHeight: 100,
        }}
        items={[
          {
            id: 'filter-project-panel',
            key: '1',
            style: { borderRadius: 0 },
            extra: type === 'project' && (
              <Flex
                gap={8}
                style={{ width: 80 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenSearchHistoryModal(true);
                }}
              >
                <HistoryOutlined />
                <Text color="primary" text={t('search-history')} />
              </Flex>
            ),
            label: (
              <>
                {!openBasicAccordion ? (
                  <>
                    <Text color="primary" text={t('search-condition')} />
                    <ProjectsSearchFieldTags onResetPayment={onResetPayment} />
                  </>
                ) : (
                  <Text color="primary" text={t('search-condition')} />
                )}
              </>
            ),
            children: (
              <>
                <SearchForm
                  onSubmit={onSubmit}
                  onResetPayment={onResetPayment}
                  resetCount={resetCount}
                />
                {/* testで使用するため */}
                {children}
              </>
            ),
          },
        ]}
      />
      <SearchHistoryModal
        // page番号をresetするため
        key={openSearchHistoryModal.toString()}
        open={openSearchHistoryModal}
        onSearch={() => {
          resetFormTrigger.current = false;
        }}
        onClose={() => {
          setOpenSearchHistoryModal(false);
        }}
      />
    </FormProvider>
  );
};
